/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { aiIntegrationsSetScheduleEnabled } from "../funcs/aiIntegrationsSetScheduleEnabled.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AIIntegrationScheduleState } from "../models/components/aiintegrationschedulestate.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetAIIntegrationScheduleEnabledRequest,
  SetAIIntegrationScheduleEnabledSecurity,
} from "../models/operations/setaiintegrationscheduleenabled.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetAIIntegrationScheduleEnabledMutationVariables = {
  request: SetAIIntegrationScheduleEnabledRequest;
  security?: SetAIIntegrationScheduleEnabledSecurity | undefined;
  options?: RequestOptions;
};

export type SetAIIntegrationScheduleEnabledMutationData =
  AIIntegrationScheduleState;

export type SetAIIntegrationScheduleEnabledMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setScheduleEnabled aiIntegrations
 *
 * @remarks
 * Enable or disable one sync schedule of a provider's org-wide AI integration config. Disabled schedules are skipped by the poller until re-enabled.
 */
export function useSetAIIntegrationScheduleEnabledMutation(
  options?: MutationHookOptions<
    SetAIIntegrationScheduleEnabledMutationData,
    SetAIIntegrationScheduleEnabledMutationError,
    SetAIIntegrationScheduleEnabledMutationVariables
  >,
): UseMutationResult<
  SetAIIntegrationScheduleEnabledMutationData,
  SetAIIntegrationScheduleEnabledMutationError,
  SetAIIntegrationScheduleEnabledMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetAIIntegrationScheduleEnabledMutation(client, options),
    ...options,
  });
}

export function mutationKeySetAIIntegrationScheduleEnabled(): MutationKey {
  return ["@gram/client", "aiIntegrations", "setScheduleEnabled"];
}

export function buildSetAIIntegrationScheduleEnabledMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetAIIntegrationScheduleEnabledMutationVariables,
  ) => Promise<SetAIIntegrationScheduleEnabledMutationData>;
} {
  return {
    mutationKey: mutationKeySetAIIntegrationScheduleEnabled(),
    mutationFn: function setAIIntegrationScheduleEnabledMutationFn({
      request,
      security,
      options,
    }): Promise<SetAIIntegrationScheduleEnabledMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(aiIntegrationsSetScheduleEnabled(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
